import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { X } from "@phosphor-icons/react";

export const TOUR_EVENT = "cr-demo-tour";
const SEEN = "cr-demo-tour-seen";

export const STEPS = [
  { intro: true, title: "Cash Flow Calibration Made Easy", body: "A two-minute look at how CalibRunway turns Stripe, your bank and QuickBooks into one live runway, so every growth move is a calculated one." },
  { sel: '[data-testid="zero-cash-date"]', title: "Zero Cash Date", body: "The exact day cash would run out at today's burn, so founders know their real deadline." },
  { sel: '[data-testid="target-zcd-btn"], [data-testid="target-gap"]', title: "Target date and gap", body: "Pick the date you need to reach (say, your next raise) and see how many months you have to close." },
  { sel: '[data-testid="runway-chart"]', title: "Runway chart", body: "Actual cash, the baseline forecast and saved scenarios on one chart. The separate dotted line is the weighted sales pipeline, never mixed into the baseline." },
  { sel: '[data-testid="tri-sync-btn"]', title: "Multi-Sync", body: "One click pulls Stripe revenue, bank balances and QuickBooks bills into the model, with duplicate protection." },
  { sel: '[data-testid="revenue-trend-card"]', title: "Revenue trend", body: "Six months of subscription MRR and cash collected, straight from Stripe." },
  { sel: '[data-testid="ai-calibrator"]', title: "AI Calibrator", body: "Ask a what-if in plain English, like \"What if we hire two engineers?\", and get the runway impact as a scenario." },
  { sel: '[data-testid="scenario-panel"]', title: "Saved scenarios", body: "Compare plans side by side. Teammates comment, and the founder marks each one Approved or Rejected." },
  { sel: '[data-testid="nav-alerts"], [data-testid="m-nav-alerts"]', title: "Board reports", body: "Monthly investor PDFs and weekly digests, generated from the same live numbers." },
];

const ACCENTS = {
  demo: { border: "border-[#B026FF]/50", label: "text-[#D9A6FF]", btn: "bg-[#B026FF] text-white hover:bg-[#C45CFF]", ring: "ring-[#B026FF] shadow-[0_0_0_4px_rgba(176,38,255,0.25)]" },
  brand: { border: "border-[#00FF9D]/50", label: "text-[#00FF9D]", btn: "bg-[#00FF9D] text-[#050505] hover:bg-[#7DFFC9]", ring: "ring-[#00FF9D] shadow-[0_0_0_4px_rgba(0,255,157,0.25)]" },
};

const visible = (s) => (s.intro || s.final ? document.body : [...document.querySelectorAll(s.sel)].find((el) => el.getClientRects().length > 0));

const Centered = ({ step, total, a, onNext, onClose }) => (
  <div data-testid={step.final ? "tour-final" : "demo-tour-intro"} role="dialog" aria-label={step.title}
    className={`fixed left-1/2 top-1/2 z-[91] w-[min(440px,calc(100vw-24px))] -translate-x-1/2 -translate-y-1/2 rounded-2xl border ${a.border} bg-[#0A0A0A] p-7 text-center shadow-[0_20px_60px_rgba(0,0,0,0.7)] animate-in fade-in-0 zoom-in-95`}>
    <img src="/calibrunway-mark.png" alt="CalibRunway" data-testid="demo-tour-logo" className="mx-auto h-14 w-14" />
    <p className={`mt-3 font-mono text-[10px] ${a.label}`}>{step.final ? "GETTING STARTED" : `CALIBRUNWAY · ${total - 1} QUICK TIPS`}</p>
    <h2 data-testid="demo-tour-pitch" className="font-display text-2xl text-white mt-2">{step.title}</h2>
    <p className="mt-2 text-sm text-[#A3A3A3] leading-relaxed">{step.body}</p>
    {step.list && (
      <ul data-testid="tour-final-list" className="mt-4 space-y-1.5 text-left text-sm text-[#CFCFCF]">
        {step.list.map((t) => <li key={t} className="flex gap-2"><span className={a.label}>→</span>{t}</li>)}
      </ul>
    )}
    <div className="mt-6 flex justify-center gap-2">
      <button data-testid="demo-tour-skip" onClick={onClose} className="rounded-full border border-[#2A2A2A] px-4 py-2 text-xs hover:border-white transition-colors">{step.final ? (step.action ? "Later" : "Close") : "Skip"}</button>
      {(!step.final || step.action) && (
        <button data-testid={step.final ? "tour-final-action" : "demo-tour-next"} onClick={step.final ? () => { onClose(); step.action.run(); } : onNext}
          className={`rounded-full px-5 py-2 text-xs font-semibold transition-colors ${a.btn}`}>{step.final ? step.action.label : "Start the tour"}</button>
      )}
    </div>
  </div>
);

const useRect = (el) => {
  const [rect, setRect] = useState(null);
  useLayoutEffect(() => {
    if (!el) return undefined;
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    const update = () => setRect(el.getBoundingClientRect());
    update();
    const t = setTimeout(update, 450);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => { clearTimeout(t); window.removeEventListener("resize", update); window.removeEventListener("scroll", update, true); };
  }, [el]);
  return rect;
};

const Card = ({ rect, step, i, tips, last, a, onNext, onBack, onClose }) => {
  const w = Math.min(340, window.innerWidth - 24);
  const tall = rect.height > window.innerHeight * 0.5;
  const beside = !tall ? null : (rect.right + w + 28 < window.innerWidth ? rect.right + 14 : rect.left - w - 14 > 12 ? rect.left - w - 14 : null);
  const below = rect.bottom + 200 < window.innerHeight;
  const top = beside != null ? Math.max(12, Math.min(rect.top + 12, window.innerHeight - 220)) : below ? rect.bottom + 14 : Math.max(12, rect.top - 14 - 190);
  const left = beside != null ? beside : Math.min(Math.max(12, rect.left), window.innerWidth - w - 12);
  return (
    <div data-testid="demo-tour-card" role="dialog" aria-label={step.title} style={{ top, left, width: w }}
      className={`fixed z-[91] rounded-2xl border ${a.border} bg-[#0A0A0A] p-5 shadow-[0_20px_60px_rgba(0,0,0,0.7)] animate-in fade-in-0 zoom-in-95`}>
      <div className="flex items-start justify-between gap-3">
        <p className={`font-mono text-[10px] ${a.label}`}>TIP {i} OF {tips}</p>
        <button data-testid="demo-tour-close" onClick={onClose} aria-label="Close tour" className="text-[#6B6B6B] hover:text-white transition-colors"><X size={14} /></button>
      </div>
      <p data-testid="demo-tour-title" className="font-display text-lg text-white mt-1">{step.title}</p>
      <p className="mt-1.5 text-sm text-[#A3A3A3] leading-relaxed">{step.body}</p>
      <div className="mt-4 flex items-center gap-2">
        <button data-testid="demo-tour-skip" onClick={onClose} className="text-xs text-[#6B6B6B] hover:text-white transition-colors">Skip tour</button>
        <div className="ml-auto flex gap-2">
          {i > 1 && <button data-testid="demo-tour-back" onClick={onBack} className="rounded-full border border-[#2A2A2A] px-3 py-1.5 text-xs hover:border-white transition-colors">Back</button>}
          <button data-testid="demo-tour-next" onClick={onNext} className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${a.btn}`}>{last ? "Finish" : "Next"}</button>
        </div>
      </div>
    </div>
  );
};

// Guided Next-button tour. autoStart: start after a short delay; onDone fires once when closed or finished.
export const Tour = ({ steps: all, autoStart, onDone, accent = "demo" }) => {
  const a = ACCENTS[accent];
  const [steps, setSteps] = useState(null);
  const [i, setI] = useState(0);
  const start = useCallback(() => { setSteps(all.filter(visible)); setI(0); }, [all]);
  const close = useCallback(() => { setSteps(null); onDone?.(); }, [onDone]);
  useEffect(() => {
    window.addEventListener(TOUR_EVENT, start);
    let t = null;
    // Wait for the cookie choice first so the two never overlap.
    const tryStart = () => { t = document.querySelector('[data-testid="cookie-consent"]') ? setTimeout(tryStart, 1000) : setTimeout(start, 1200); };
    if (autoStart) tryStart();
    return () => { window.removeEventListener(TOUR_EVENT, start); clearTimeout(t); };
  }, [start, autoStart]);
  useEffect(() => {
    if (!steps) return undefined;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [steps, close]);
  const step = steps?.[i];
  const el = step && !step.intro && !step.final ? visible(step) : null;
  const rect = useRect(el);
  if (step?.intro || step?.final) return (
    <>
      <div data-testid="demo-tour-overlay" onClick={close} className="fixed inset-0 z-[89] bg-black/60 backdrop-blur-sm" />
      <Centered step={step} total={steps.filter((s) => !s.final).length} a={a} onClose={close} onNext={() => setI(1)} />
    </>
  );
  if (!step || !rect) return null;
  const tips = steps.filter((s) => !s.intro && !s.final).length;
  const pad = 6;
  return (
    <>
      <div data-testid="demo-tour-overlay" onClick={close} className="fixed inset-0 z-[89] bg-black/40" />
      <div data-testid="demo-tour-highlight" className={`pointer-events-none fixed z-[90] rounded-xl ring-2 transition-all duration-300 ${a.ring}`}
        style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }} />
      <Card rect={rect} step={step} i={i} tips={tips} last={i + 1 === steps.length} a={a} onClose={close}
        onBack={() => setI(i - 1)} onNext={() => (i + 1 === steps.length ? close() : setI(i + 1))} />
    </>
  );
};

const markDemoSeen = () => sessionStorage.setItem(SEEN, "1");
export const DemoTour = () => <Tour steps={STEPS} autoStart={!sessionStorage.getItem(SEEN)} onDone={markDemoSeen} />;
